import React from 'react';
import {useTranslation} from 'react-i18next';
import {FaIcon} from '../FaIcon';
import {CHANNEL_INFO_BY_CHANNEL} from '../channels/channels';
import {consolidateWarnings, WARNING_CTA_CODES} from './state/renderWarnings';

/**
 * El aviso de arriba del folio cuando uno o más bloques no se pudieron calcular: qué pasó, a
 * cuántos bloques afecta y —sólo para la agencia— qué hacer.
 *
 * Dos audiencias, dos namespaces de i18n, y NO un `if` adentro del copy: la agencia lee «reconectá
 * la cuenta» y el cliente del portal lee «tu agencia ya está al tanto». Separarlos evita que el
 * texto del portal se degrade la próxima vez que alguien edite el de la agencia.
 *
 * El detalle de qué cuentas cayeron (`disconnected_accounts`) **sólo viaja en el payload de los
 * endpoints de agencia**: en el portal el dato no existe, así que el aislamiento no depende de que
 * este componente se acuerde de esconderlo.
 */
/**
 * Cuántas cuentas se nombran antes de resumir en «y N más».
 *
 * ⚠️ El tope NO es cosmético. Medido en producción el 21/09 con la agencia 130: el cliente del caso
 * tiene **32 cuentas** sin acceso, y sin tope el aviso medía **711 px — el 100% del viewport**, con
 * el primer bloque del informe empezando en y=1140. O sea que había que desplazar una pantalla
 * entera para ver un solo panel del informe que se vino a leer.
 *
 * Es el mismo error que el aviso tenía el día anterior, en el otro extremo: primero no nombraba
 * NINGUNA cuenta (el filtro las comía), y al arreglarlo pasó a nombrarlas todas. Con 1-3 cuentas
 * —el caso normal— nombrarlas es exactamente lo útil; con 32 no ayuda a nadie, porque para
 * reconectarlas hay que ir a Conexiones igual, que es lo que ofrece el botón.
 */
export const MAX_ACCOUNTS_LISTED = 5;

export function RenderWarningsNotice({renderResult, agencyClientId = null, showCta = false,
                                      showAccountDetail = showCta,
                                      namespace = 'informes.warnings'}) {
    const {t, i18n} = useTranslation();
    const summary = consolidateWarnings(renderResult);
    if (!summary) return null;

    const {copyCode, code, affected, total, allPanels, showScope} = summary;
    const title = t(`${namespace}.${copyCode}.title`, t(`${namespace}.GENERIC.title`));
    const body = t(`${namespace}.${copyCode}.body`, t(`${namespace}.GENERIC.body`));
    // El alcance también respeta el namespace: estaba fijo en `informes.warnings.*`, así que editar
    // «Afecta a N de M bloques» para la agencia le cambiaba la frase al cliente del portal en
    // silencio — justo lo que los dos namespaces existen para evitar (review R-reports-fe-10).
    const scope = !showScope ? null : (allPanels
        ? t(`${namespace}.scope_all`, 'Afecta a todo el informe.')
        : t(`${namespace}.scope_some`, {
            affected, total, defaultValue: 'Afecta a {{affected}} de {{total}} bloques.',
        }));

    const accounts = (renderResult && renderResult.disconnected_accounts) || [];
    const href = agencyClientId ? `/client/${agencyClientId}/conections` : null;
    const withCta = showCta && !!href && WARNING_CTA_CODES.has(code);

    return (
        <div className="inf-warning-notice" role="status">
            {/* `unlink` y no `link-slash`: el webfont del repo es FA5 y el nombre FA6 no está en
                `init_icons_library.js`, así que no dibujaría NADA y sin error. */}
            <FaIcon iconName="unlink" className="inf-warning-notice__icon"/>
            <div className="inf-warning-notice__text">
                <strong className="inf-warning-notice__title">{title}</strong>
                <p className="inf-warning-notice__body">{scope ? `${body} ${scope}` : body}</p>
                {/* El nombre de la cuenta sale de los modelos de Dashbo, no de parsear el mensaje
                    del nodo: ver `reports/services/disconnected_accounts.py`.

                    Gateado por `showAccountDetail` y NO por `showCta`: eran la misma bandera, así
                    que el día que alguien quisiera un CTA en el portal («Avisale a tu agencia»),
                    prenderlo habría filtrado los nombres de cuenta de la agencia al cliente
                    (review R-reports-fe-09). El aislamiento de verdad lo da el payload —el backend
                    no calcula `disconnected_accounts` para el portal—; esto es la segunda línea. */}
                {showAccountDetail && accounts.length > 0 && (
                    <ul className="inf-warning-notice__accounts">
                        {accounts.slice(0, MAX_ACCOUNTS_LISTED).map((account, index) => (
                            <li key={`${account.name}-${index}`}>{accountLine(account, t, i18n.language)}</li>
                        ))}
                        {accounts.length > MAX_ACCOUNTS_LISTED && (
                            <li className="inf-warning-notice__accounts-more">
                                {t('informes.warnings.accounts_more', {
                                    count: accounts.length - MAX_ACCOUNTS_LISTED,
                                    defaultValue: 'y {{count}} cuentas más',
                                })}
                            </li>
                        )}
                    </ul>
                )}
            </div>
            {withCta && (
                <a href={href} className="btn -alt inf-warning-notice__cta">
                    {t(`${namespace}.${copyCode}.cta`, 'Revisar conexiones')}
                </a>
            )}
        </div>
    );
}

export function accountLine(account, t, language) {
    const channelInfo = CHANNEL_INFO_BY_CHANNEL[account.channel];
    const channel = (channelInfo && channelInfo.Spanish) || account.channel;
    const since = formatDate(account.no_access_since, language);
    const options = {name: account.name, channel, interpolation: {escapeValue: false}};
    if (!since) {
        return t('informes.warnings.account_line_no_date', {...options, defaultValue: '{{name}} ({{channel}})'});
    }
    return t('informes.warnings.account_line', {
        ...options, since, defaultValue: '{{name}} ({{channel}}) — sin acceso desde el {{since}}',
    });
}

/**
 * La fecha llega en ISO (`YYYY-MM-DD`) y se formatea con el locale de la pantalla.
 *
 * Se parte a mano en vez de `new Date(iso)`: ese constructor interpreta un ISO sin hora como
 * medianoche UTC, y en un huso negativo —el nuestro— `toLocaleDateString` devuelve el día ANTERIOR.
 * Una fecha de «desde cuándo no tenemos acceso» corrida un día se lee como un dato mal traído.
 */
function formatDate(isoDate, language) {
    if (!isoDate) return null;
    const parts = String(isoDate).split('-');
    if (parts.length !== 3) return null;
    const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString(language || 'es');
}
